import { Chip, ChipBackgroundColor, ChipSize } from "@amzn/stencil-react-components/chip";
import { Row, View } from "@amzn/stencil-react-components/layout";
import { Text, TextVariant } from "@amzn/stencil-react-components/text";
import { TooltipAlignment, withTooltip } from "@amzn/stencil-react-components/tooltip";
import { IconInformationFillExtraSmall } from "@amzn/stencil-react-icons";
import { StarAverageRating, StarAverageRatingSize } from "common/components/Ratings";
import { formatDate } from "common/helpers";
import React, { FC } from "react";

export enum ReviewType {
  USER = "User review",
  AI = "AI review",
}

/**
 * Props for the ProductReviewCard component.
 * Renders a single user or AI review with title, content, score, date, and category tags.
 */
export interface ProductReviewCardProps {
  /** Review category tags (e.g., "Features", "Usability") */
  categories: string[];
  /** Review body text */
  content: string;
  /** Review title/headline */
  title: string;
  /** Custom data-test-id for the card container */
  dataTestId?: string;
  /** Whether this review belongs to the current user */
  isSelf?: boolean;
  /** Author's job family name */
  jobFamily?: string;
  /** Date the review was published */
  publishedDate?: Date;
  /** Distinguishes between user and AI reviews */
  reviewType?: ReviewType;
  /** Author's star rating (1-5) */
  score?: number;
  /** Number of upvotes (currently disabled) */
  votes?: number;
}

export const ProductReviewCard: FC<ProductReviewCardProps> = ({
  title,
  categories,
  votes,
  isSelf = false,
  content,
  dataTestId,
  publishedDate,
  reviewType,
  jobFamily,
  score,
}) => {
  const JobFamilyChip = () => (
    <Chip
      backgroundColor={ChipBackgroundColor.Purple}
      size={ChipSize.ExtraSmall}
      dataTestId="product-review-chip-job-family"
    >
      <Row alignItems="center" gridGap={"dimensions.spacing.50"}>
        <IconInformationFillExtraSmall /> <Text>{`${jobFamily} feedback`}</Text>
      </Row>
    </Chip>
  );

  const SelfReviewChip = () => (
    <Chip backgroundColor={ChipBackgroundColor.Purple} size={ChipSize.ExtraSmall} dataTestId="product-review-chip-self">
      <Text>Your review</Text>
    </Chip>
  );

  const renderReviewTypeChip = () => {
    if (reviewType) {
      return (
        <Chip backgroundColor={ChipBackgroundColor.Blue} size={ChipSize.ExtraSmall} dataTestId="product-review-chip">
          <Text variant={TextVariant.BodySMInteractive}>{reviewType}</Text>
        </Chip>
      );
    }
  };

  const renderChips = () => {
    const chips: JSX.Element[] = [];
    if (isSelf) {
      chips.push(<SelfReviewChip />);
    }
    if (jobFamily) {
      const JobFamilyChipWithTooltip = withTooltip()(JobFamilyChip);
      chips.push(
        <JobFamilyChipWithTooltip
          tooltipText="The author of this review belongs to this job family."
          tooltipAlignment={TooltipAlignment.Left}
        />
      );
    }

    categories.forEach((category) => {
      chips.push(
        <Chip
          backgroundColor={ChipBackgroundColor.Blue}
          size={ChipSize.ExtraSmall}
          dataTestId={`product-review-chip-category-${category}`}
        >
          <Text>{category}</Text>
        </Chip>
      );
    });
    return chips;
  };

  return (
    <View style={{ marginTop: "16px", marginBottom: "8px" }} dataTestId={dataTestId ?? "product-review-card"}>
      {renderReviewTypeChip()}
      <Row justifyContent="space-between" style={{ marginTop: "16px", marginBottom: "8px", wordBreak: "break-word" }}>
        <Text variant={TextVariant.HeadingSM} dataTestId="product-review-title">
          {title.charAt(0).toUpperCase() + title.slice(1)}
        </Text>
        {/* DISABLED UNTIL EVERYTHING IS ENABLED */}
        {/* <Row alignItems="center">
          <IconThumbUpExtraSmall />
          <Text>{votes}</Text>
        </Row> */}
      </Row>
      {score != null && (
        <Row dataTestId="product-review-score" style={{ marginBottom: "8px" }}>
          <StarAverageRating size={StarAverageRatingSize.ExtraSmall} averageRating={score} hideScore />
        </Row>
      )}
      <Text style={{ wordBreak: "break-word" }} dataTestId="product-review-content">
        {content.charAt(0).toUpperCase() + content.slice(1)}
      </Text>
      {publishedDate && (
        <Text fontSize="T100" color="neutral70" dataTestId="product-review-date" style={{ marginTop: "8px" }}>
          {formatDate(publishedDate)}
        </Text>
      )}
      <Row
        flexWrap="wrap"
        rowGap="dimensions.spacing.200"
        columnGap="dimensions.spacing.200"
        padding={["dimensions.spacing.250", 0, "dimensions.spacing.250", 0]}
      >
        {renderChips()}
      </Row>
    </View>
  );
};
